import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ApercuFlottant from './ApercuFlottant';
import useTelephone from '../hooks/useTelephone';
import { BOUTON_BARRE, LIBELLE_BARRE, PASTILLE_BARRE } from './barreBoutons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  if (target.getTime() === today.getTime()) return "Aujourd'hui";
  if (target.getTime() === tomorrow.getTime()) return 'Demain';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const isOverdue = (t) => {
  if (!t.due_date || t.status === 'done') return false;
  return new Date(t.due_date) < new Date();
};

/**
 * Cloche de notifications de tâches.
 * - Survol : ouverture du popover avec les 5 prochaines
 * - Clic : navigation vers /taches
 * - Badge : nombre total de tâches à faire (todo + in_progress)
 *   • rouge si au moins une en retard
 *   • bleu sinon
 */
const TaskBell = () => {
  const navigate = useNavigate();
  const telephone = useTelephone(); // lot DK2 — pas d'aperçu au toucher
  const [badge, setBadge] = useState({ overdue: 0, today: 0, todo: 0 });
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const fetchBadge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks/badge`);
      setBadge(res.data || { overdue: 0, today: 0, todo: 0 });
    } catch {
      // silencieux
    }
  }, []);

  const fetchUpcoming = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks/upcoming`, { params: { limit: 5 } });
      setUpcoming(res.data || []);
    } catch {
      setUpcoming([]);
    }
  }, []);

  useEffect(() => {
    fetchBadge();
    const interval = setInterval(fetchBadge, 60000);
    const onTasksChanged = () => { fetchBadge(); fetchUpcoming(); };
    window.addEventListener('tasks-changed', onTasksChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('tasks-changed', onTasksChanged);
    };
  }, [fetchBadge, fetchUpcoming]);

  useEffect(() => {
    if (open) fetchUpcoming();
  }, [open, fetchUpcoming]);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(true);
  };
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 200);
  };

  const total = badge.todo || 0;
  const badgeColor = badge.overdue > 0 ? 'bg-red-500' : 'bg-blue-500';

  const handleComplete = async (taskId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`);
      fetchBadge();
      fetchUpcoming();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      // silent
    }
  };

  return (
    <div
      className="relative"
      ref={containerRef}
      onMouseEnter={telephone ? undefined : handleMouseEnter}
      onMouseLeave={telephone ? undefined : handleMouseLeave}
    >
      <button
        data-testid="btn-taches"
        type="button"
        onClick={() => { setOpen(false); navigate('/taches'); }}
        className={BOUTON_BARRE}
        title="Tâches"
        aria-label="Tâches"
      >
        <i className="bi bi-check2-square text-[19px] leading-none text-[#007BFF]"></i>
        <span className={LIBELLE_BARRE}>Tâches</span>
        {total > 0 && (
          <span
            className={`${PASTILLE_BARRE} ${badgeColor}`}
            data-testid="task-bell-badge"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <ApercuFlottant
          ancreRef={containerRef}
          onFermer={() => setOpen(false)}
          largeur={320}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
        <div
          className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          data-testid="task-bell-popover"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <i className="bi bi-check2-square text-blue-600"></i>
              <span className="text-sm font-semibold text-slate-800">Mes tâches</span>
            </div>
            {(badge.overdue > 0 || badge.today > 0) ? (
              <span className="text-[11px] text-slate-500">
                {badge.overdue > 0 && (
                  <span className="text-red-600 font-semibold">{badge.overdue} en retard</span>
                )}
                {badge.overdue > 0 && badge.today > 0 && <span> · </span>}
                {badge.today > 0 && <span>{badge.today} aujourd'hui</span>}
              </span>
            ) : total > 0 ? (
              <span className="text-[11px] text-slate-500">{total} à faire</span>
            ) : null}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {upcoming.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                <i className="bi bi-clipboard-check text-2xl text-slate-300 block mb-2"></i>
                Aucune tâche en attente
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {upcoming.map((t) => {
                  const overdue = isOverdue(t);
                  return (
                    <li
                      key={t.id}
                      onClick={() => { setOpen(false); navigate('/taches'); }}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-start gap-3"
                      data-testid={`task-bell-item-${t.id}`}
                    >
                      <button
                        onClick={(e) => handleComplete(t.id, e)}
                        className="mt-0.5 w-4 h-4 rounded border-2 border-slate-300 hover:border-emerald-500 shrink-0"
                        title="Marquer terminée"
                        aria-label="Marquer terminée"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{t.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {t.due_date && (
                            <span className={`text-[11px] ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                              <i className="bi bi-calendar3 mr-1"></i>{formatDateShort(t.due_date)}
                              {overdue && ' · En retard'}
                            </span>
                          )}
                          {t.priority === 'high' && (
                            <span className="text-[10px] uppercase font-semibold text-red-600">Haute</span>
                          )}
                          {t.status === 'in_progress' && (
                            <span className="text-[10px] uppercase font-semibold text-amber-600">En cours</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-100 p-2 bg-slate-50">
            <button
              onClick={() => { setOpen(false); navigate('/taches'); }}
              className="w-full text-center text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-md py-2 transition"
              data-testid="task-bell-see-all"
            >
              Voir toutes les tâches <i className="bi bi-arrow-right ml-1"></i>
            </button>
          </div>
        </div>
        </ApercuFlottant>
      )}
    </div>
  );
};

export default TaskBell;
